import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { getWebsiteEnquiries } from "../Apis/WebsiteEnquiryAPIs";
import { getPresentExpos } from "../Apis/ExpoAPIs";
import { formatDate } from "../helper/DateHelper";
import TablePagination from "./TablePagination";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";

const InterestListLayer = () => {
  const navigate = useNavigate();
  const [enquiryList, setEnquiryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("Stall"); // Stall or Visitor
  const [expoOptions, setExpoOptions] = useState([]);
  const [selectedExpo, setSelectedExpo] = useState(null);

  // Details Modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [stallCount, setStallCount] = useState(0);
  const [visitorCount, setVisitorCount] = useState(0);

  // Fetch expos for filtering
  useEffect(() => {
    const fetchExpos = async () => {
      try {
        const res = await getPresentExpos({ limit: 100 });
        if (res.status) {
          const list = res.response?.data || res.response || [];
          setExpoOptions(
            Array.isArray(list)
              ? list.map((e) => ({ value: e._id || e.id, label: e.expoName }))
              : []
          );
        }
      } catch (error) {
        console.error("Error fetching expos:", error);
      }
    };
    fetchExpos();
  }, []);


  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: rowsPerPage,
          type: activeTab,
          expoId: selectedExpo?.value || undefined,
          search: searchTerm || undefined
        };

        const res = await getWebsiteEnquiries(params);
        if (res.status) {
          if (res.response && Array.isArray(res.response.data)) {
            setEnquiryList(res.response.data);
            setTotalRecords(res.response.total || 0);
            if (activeTab === "Stall") {
              setStallCount(res.response.total || 0);
            } else {
              setVisitorCount(res.response.total || 0);
            }
          } else {
            const data = res.response || [];
            setEnquiryList(data);
            setTotalRecords(data.length);
            if (activeTab === "Stall") {
              setStallCount(data.length);
            } else {
              setVisitorCount(data.length);
            }
          }
        }

        const otherTab = activeTab === "Stall" ? "Visitor" : "Stall";
        const countParams = {
          page: 0,
          limit: 1,
          type: otherTab,
          expoId: selectedExpo?.value || undefined,
          search: searchTerm || undefined
        };

        const countRes = await getWebsiteEnquiries(countParams);
        if (countRes.status) {
          const total = countRes.response?.total || (Array.isArray(countRes.response?.data) ? countRes.response.data.length : countRes.response?.length || 0);
          if (otherTab === "Stall") {
            setStallCount(total);
          } else {
            setVisitorCount(total);
          }
        }
      } catch (error) {
        console.error("Error fetching website enquiry data:", error);
        toast.error("Failed to fetch website enquiries");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [currentPage, rowsPerPage, searchTerm, activeTab, selectedExpo]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
  };

  const handleShowDetails = (item) => {
    setSelectedEnquiry(item);
    setShowDetailModal(true);
  };

  const handleConvertClick = (item) => {
    const targetExpoId = item.expoId?._id || item.expoId;
    if (activeTab === "Stall") {
      navigate(
        `/present-expo?expoId=${targetExpoId}&openModal=true&name=${encodeURIComponent(
          item.name
        )}&mobile=${encodeURIComponent(item.mobileNo)}&email=${encodeURIComponent(
          item.email || ""
        )}&companyName=${encodeURIComponent(item.companyName || "")}`
      );
    } else {
      navigate(
        `/visitor-reports?tab=enquiry&expoId=${targetExpoId}&openModal=true&name=${encodeURIComponent(
          item.name
        )}&phone=${encodeURIComponent(item.mobileNo)}&email=${encodeURIComponent(
          item.email || ""
        )}&companyName=${encodeURIComponent(item.companyName || "")}`
      );
    }
  };

  const handleClearFilters = () => {
    setSelectedExpo(null);
    setSearchTerm("");
    setCurrentPage(0);
  };

  // const hasActiveFilters = selectedExpo || searchTerm;

  return (
    <div className="card h-100 p-0 radius-12 border-0 shadow-sm">
      {/* Header */}
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{
          background:
            "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)",
        }}
      >
        <h6 className="text-white mb-0 fw-bold">Website Enquiries</h6>
      </div>

      {/* Filter Bar */}
      <div
        className="px-24 py-16 border-bottom"
        style={{ backgroundColor: "#fafafa" }}
      >
        <div className="d-flex align-items-end flex-wrap gap-3">
          {/* Expo Name Dropdown */}
          <div style={{ minWidth: "220px", flex: "1 1 220px", maxWidth: "280px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Exhibition Name
            </label>
            <Select
              options={expoOptions}
              value={selectedExpo}
              onChange={(val) => {
                setSelectedExpo(val);
                setCurrentPage(0);
              }}
              isClearable
              placeholder="All Exhibitions"
              styles={selectStyles()}
            />
          </div>

          {/* Search Input */}
          <div style={{ minWidth: "200px", flex: "1 1 200px", maxWidth: "260px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Search
            </label>
            <div className="position-relative">
              <input
                type="text"
                className="form-control radius-8 ps-40"
                style={{ height: "44px" }}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <Icon
                icon="ion:search-outline"
                className="position-absolute top-50 translate-middle-y text-secondary-light"
                style={{ fontSize: "18px", left: "12px" }}
              />
            </div>
          </div>

          {/* Refresh & Clear Filters */}
         
          
             <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-outline-danger h-44-px w-100 radius-8 fw-bold d-flex align-items-center justify-content-center gap-2"
                onClick={handleClearFilters}
                title="Clear Filters"
              >
                <Icon icon="solar:restart-bold" className="text-xl" />
                Reset
              </button>
            </div>
        </div>
      </div>

      <div className="card-body p-0">
        {/* Tab Selection matching VisitorReports tab design */}
        <div className="d-flex w-100">
          {[
            { id: "Stall", label: "Stall Booking", icon: "solar:layout-grid-line-bold", count: stallCount },
            { id: "Visitor", label: "Visitor Registration", icon: "solar:users-group-two-rounded-bold", count: visitorCount },
          ].map((tab) => (
            <button
              key={tab.id}
              className={`w-50 py-20 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${activeTab === tab.id ? "text-danger-600 bg-white" : "text-secondary-light bg-neutral-50"
                }`}
              onClick={() => handleTabChange(tab.id)}
              style={{
                border: activeTab === tab.id ? "1px solid #ed1c24" : "1px solid #e9ecef",
                borderTop: activeTab === tab.id ? "4px solid #ed1c24" : "1px solid #e9ecef",
                borderBottom: activeTab === tab.id ? "0" : "1px solid #e9ecef",
                zIndex: activeTab === tab.id ? 2 : 1,
              }}
            >
              <Icon icon={tab.icon} className="text-lg" />
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        <div className="p-24">
          <div className="table-responsive border radius-12 shadow-sm mb-4">
            <table className="table bordered-table sm-table mb-0 text-nowrap" style={{ minWidth: '1500px' }}>
              <thead>
                <tr className="bg-light border-bottom">
                  <th scope="col" className="text-dark-main fw-bold">S.No</th>
                  <th scope="col" className="text-dark-main fw-bold">Exhibition Name</th>
                  <th scope="col" className="text-dark-main fw-bold">Name</th>
                  <th scope="col" className="text-dark-main fw-bold">Mobile Number</th>
                  <th scope="col" className="text-dark-main fw-bold">Email ID</th>
                  {activeTab === "Stall" ? (
                    <>
                      <th scope="col" className="text-dark-main fw-bold">Company Name</th>
                      <th scope="col" className="text-dark-main fw-bold">Business Category</th>
                      <th scope="col" className="text-dark-main fw-bold">Stall Size</th>
                    </>
                  ) : (
                    <>
                      <th scope="col" className="text-dark-main fw-bold">Product of Interest</th>
                    </>
                  )}
                  <th scope="col" className="text-dark-main fw-bold">City</th>
                  <th scope="col" className="text-dark-main fw-bold">Date</th>
                  <th scope="col" className="text-dark-main fw-bold text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activeTab === "Stall" ? "11" : "9"} className="text-center py-24">
                      <div className="spinner-border spinner-border-sm text-danger" role="status"></div>
                      <span className="ms-2">Loading enquiries...</span>
                    </td>
                  </tr>
                ) : enquiryList.length > 0 ? (
                  enquiryList.map((item, index) => (
                    <tr key={item._id || item.id}>
                      <td>{currentPage * rowsPerPage + index + 1}</td>
                      <td className="fw-medium">{item?.expoId?.expoName || "N/A"}</td>
                      <td>{item.name}</td>
                      <td>{item.mobileNo}</td>
                      <td>{item.email || "N/A"}</td>
                      {activeTab === "Stall" ? (
                        <>
                          <td>{item.companyName || "N/A"}</td>
                          <td>{item.businessCategory || "N/A"}</td>
                          <td>{item.stallSize || "N/A"}</td>
                        </>
                      ) : (
                        <>
                          <td>{item.productOfInterest || "N/A"}</td>
                        </>
                      )}
                      <td>{item.city}</td>
                      <td>{formatDate(item.createdAt)}</td>
                      <td>
                        <div className="d-flex align-items-center justify-content-center gap-10">
                          <button
                            type="button"
                            onClick={() => handleShowDetails(item)}
                            className="btn-view-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            style={{
                              backgroundColor: "#eef2ff",
                              color: "#4338ca",
                              border: "1px solid #e0e7ff",
                            }}
                            title="View Details"
                          >
                            <Icon icon="lucide:eye" className="menu-icon" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleConvertClick(item)}
                            className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            title="Edit / Convert to Enquiry"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={activeTab === "Stall" ? "11" : "9"} className="text-center py-24">
                      <div className="text-secondary-light">No records found</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <TablePagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalRecords / rowsPerPage)}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            totalRecords={totalRecords}
          />
        </div>
      </div>

      {/* Details Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold text-dark">Website Enquiry Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-24">
          {selectedEnquiry && (
            <div className="row g-3">
              <div className="col-md-6 border-bottom pb-2">
                <span className="text-secondary-light text-xs fw-bold block-label">EXHIBITION</span>
                <div className="text-dark fw-medium text-sm mt-1">
                  {selectedEnquiry.expoId?.expoName || "N/A"}
                </div>
              </div>
              <div className="col-md-6 border-bottom pb-2">
                <span className="text-secondary-light text-xs fw-bold block-label">TYPE</span>
                <div className="text-dark fw-medium text-sm mt-1">
                  <span className={`badge ${selectedEnquiry.type === "Stall" ? "bg-danger" : "bg-primary"} text-white px-8 py-4 radius-4`}>
                    {selectedEnquiry.type === "Stall" ? "Stall Booking" : "Visitor Registration"}
                  </span>
                </div>
              </div>
              <div className="col-md-6 border-bottom pb-2">
                <span className="text-secondary-light text-xs fw-bold block-label">FULL NAME</span>
                <div className="text-dark fw-medium text-sm mt-1">{selectedEnquiry.name}</div>
              </div>
              <div className="col-md-6 border-bottom pb-2">
                <span className="text-secondary-light text-xs fw-bold block-label">MOBILE NUMBER</span>
                <div className="text-dark fw-medium text-sm mt-1">{selectedEnquiry.mobileNo}</div>
              </div>
              <div className="col-md-6 border-bottom pb-2">
                <span className="text-secondary-light text-xs fw-bold block-label">EMAIL ID</span>
                <div className="text-dark fw-medium text-sm mt-1">{selectedEnquiry.email || "N/A"}</div>
              </div>
              <div className="col-md-6 border-bottom pb-2">
                <span className="text-secondary-light text-xs fw-bold block-label">CITY</span>
                <div className="text-dark fw-medium text-sm mt-1">{selectedEnquiry.city}</div>
              </div>
              {selectedEnquiry.type === "Stall" ? (
                <>
                  <div className="col-md-6 border-bottom pb-2">
                    <span className="text-secondary-light text-xs fw-bold block-label">COMPANY NAME</span>
                    <div className="text-dark fw-medium text-sm mt-1">{selectedEnquiry.companyName || "N/A"}</div>
                  </div>
                  <div className="col-md-6 border-bottom pb-2">
                    <span className="text-secondary-light text-xs fw-bold block-label">BUSINESS CATEGORY</span>
                    <div className="text-dark fw-medium text-sm mt-1">{selectedEnquiry.businessCategory || "N/A"}</div>
                  </div>
                  <div className="col-md-6 border-bottom pb-2">
                    <span className="text-secondary-light text-xs fw-bold block-label">STALL SIZE</span>
                    <div className="text-dark fw-medium text-sm mt-1">{selectedEnquiry.stallSize || "N/A"}</div>
                  </div>
                  <div className="col-md-12">
                    <span className="text-secondary-light text-xs fw-bold block-label">REMARKS</span>
                    <div className="text-dark fw-medium text-sm mt-1 bg-light p-12 radius-8 border">
                      {selectedEnquiry.remarks || "No remarks provided"}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-md-6 border-bottom pb-2">
                    <span className="text-secondary-light text-xs fw-bold block-label">COMPANY NAME</span>
                    <div className="text-dark fw-medium text-sm mt-1">{selectedEnquiry.companyName || "N/A"}</div>
                  </div>
                  <div className="col-md-6 border-bottom pb-2">
                    <span className="text-secondary-light text-xs fw-bold block-label">PRODUCT OF INTEREST</span>
                    <div className="text-dark fw-medium text-sm mt-1">{selectedEnquiry.productOfInterest || "N/A"}</div>
                  </div>
                </>
              )}
              <div className="col-md-6 pt-2">
                <span className="text-secondary-light text-xs fw-bold block-label">SUBMITTED ON</span>
                <div className="text-dark fw-medium text-sm mt-1">{formatDate(selectedEnquiry.createdAt)}</div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
          {selectedEnquiry && (
            <Button variant="danger" onClick={() => { setShowDetailModal(false); handleConvertClick(selectedEnquiry); }}>
              Convert to Enquiry
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InterestListLayer;
